<template>
    <!-- Page Content -->
    <div id="page-content-wrapper" class="w-100">
        <div class="dashboard-main">
        <nav class="dt-fixed-nav">
            <div class="dt-user-info">
                <span class="dt-user-avatar">
                    <fa :icon="['fas', 'user']" />
                </span>
                <span class="dt-user-greeting">
                    <span class="dt-user-greeting-label">Welcome back</span>
                    <span class="dt-user-greeting-name">{{ user ? user.name : '' }}</span>
                </span>
            </div>
            <div class="dt-nav-actions">
                   <button 
                      class="notif-bell-btn" 
                      @click="notifPanelOpen = !notifPanelOpen" 
                      :class="{ 'notif-bell-btn--active': notifPanelOpen, 'notif-bell-btn--pulse': notifUnreadCount > 0 && !notifPanelOpen }"
                    >
                        <fa :icon="['fas', 'bell']" />
                        <span v-if="notifUnreadCount > 0" class="notif-bell-badge">{{ notifUnreadCount > 99 ? '99+' : notifUnreadCount }}</span>
                    </button>
                <button
                  v-if="globalIsUserAllowed('Release_History')"
                  class="dt-nav-btn"
                  title="Release History"
                  @click="$bvModal.show('PTVHistoryModal')"
                >
                    <fa :icon="['fas', 'history']" />
                </button>
                 <button
                  v-if="globalIsUserAllowed('Change_Password')"
                  class="dt-nav-btn"
                  :disabled="isTestAccount"
                  :title="isTestAccount ? 'Cannot change password of test account' : 'Change Password'"
                  @click="$bvModal.show('PTVChangePasswordModal')"
                >
                    <fa :icon="['fas', 'key']" />
                </button>
                <button class="dt-nav-btn dt-signout-btn" title="Sign Out" @click="signOut">
                    <fa :icon="['fas', 'sign-out-alt']" />
                </button>
            </div>
        </nav>
        <div class="dashboard-scroll">
        <div class="container-fluid mt-4">
            <!-- Header + Request Sample button -->
            <div class="dash-header mb-4">
                <div class="dash-header-text">
                    <h5 class="dash-title">Data Requests Overview</h5>
                    <p class="dash-subtitle">Track and manage your dataset delivery requests</p>
                </div>
                <div class="dash-header-actions">
                    <button type="button" class="btn btn-request-sample" @click="callMap()">
                        <fa :icon="['fas', 'plus']" class="mr-2" />
                        Request Data
                    </button>
                </div>
            </div>
            <!-- Charts Section -->
            <div class="row chart-margin">
                <div class="col-lg-12">
                    <div class="table-wrapper">
                        <div class="table-header">
                            <div class="table-header-left">
                                <span class="table-header-title">Requests Overview</span>
                            </div>
                            <div class="table-header-right">
                                <fa :icon="['fas', 'layer-group']" />
                            </div>
                        </div>
                        <div class="ptv-table-scroll" :style="{ maxHeight: tableHeight }" ref="tableScroll">
                            <vue-good-table ref="ptvDashboardTable" id="ptvDashboardTable" class="" :rows="filteredRows"
                                :columns="tableColumns" :pagination-options="pagination" :sort-options="{
                                    enabled: true,
                                    initialSortBy: { field: 'id', type: 'desc' }
                                }" styleClass="vgt-table">
                                <!-- Custom Date Filters -->
                                <template #column-filter="props">
                                    <b-form-input v-if="props.column.field == 'created_date'"
                                        @input="(val) => dateFilter(val, 'created_date')" type="date"
                                        placeholder="Select Requested Date" format="mm/dd/yyyy"></b-form-input>
                                    <b-form-input v-else-if="props.column.field == 'delivery_date'"
                                        @input="(val) => dateFilter(val, 'delivery_date')"
                                        placeholder="Select Delivery Date" type="date"
                                        format="mm/dd/yyyy"></b-form-input>
                                    <b-form-select
                                        v-else-if="props.column.field === 'created_by' && user && user.isAdmin"
                                        v-model="filters.created_by" :options="userOptions" placeholder="Select User"
                                        @change="applyAllFilters" class="w-100">
                                        <template #first>
                                            <b-form-select-option :value="null">All Users</b-form-select-option>
                                        </template>
                                    </b-form-select>
                                    <b-form-select
                                        v-else-if="props.column.field === 'status' && user && user.isAdmin"
                                        v-model="filters.status" :options="statuses" placeholder="Select Status"
                                        @change="applyAllFilters" class="w-100">
                                        <template #first>
                                            <b-form-select-option :value="null">Any</b-form-select-option>
                                        </template>
                                    </b-form-select>
                                </template>

                                <template #table-row="props">
                                    <span v-if="props.column.field == 'wkt'" style="width: 50px;"
                                        @click="initStatsModal(props.row)">
                                        <fa :icon="['fas', 'info-circle']" size="lg" style="color: #4A74A8" />
                                    </span>
                                    <!-- STATUS BADGE / CUSTOM DROPDOWN -->
                                    <span v-else-if="props.column.field === 'status' && user && user.isAdmin" class="status-cell">
                                        <div class="status-wrapper" @click.stop>
                                            <span
                                                class="status-badge"
                                                :class="[statusClass(props.row.status), { 'badge-open': editingStatusRowId === props.row.id }]"
                                                @click="toggleStatusDropdown(props.row.id, $event)"
                                                title="Click to change status"
                                            >
                                                <span class="status-dot"></span>
                                                {{ props.row.status || '—' }}
                                                <fa :icon="['fas', 'chevron-down']" class="status-chevron" :class="{ 'chevron-open': editingStatusRowId === props.row.id }" />
                                            </span>
                                            <span v-if="statusSaving[props.row.id] && statusSaving[props.row.id].saved && statusSaving[props.row.id].spinner" class="status-save-indicator status-save-indicator--saved">
                                                <fa :icon="['fas', 'check']" size="sm" />
                                            </span>
                                            <span v-else-if="statusSaving[props.row.id] && statusSaving[props.row.id].spinner" class="status-save-indicator">
                                                <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                            </span>
                                            <div
                                                v-if="editingStatusRowId === props.row.id"
                                                class="status-dropdown"
                                                :style="{ top: statusDropdownPos.top + 'px', left: statusDropdownPos.left + 'px' }"
                                            >
                                                <div
                                                    v-for="s in statuses"
                                                    :key="s.value"
                                                    class="status-option"
                                                    :class="statusClass(s.value)"
                                                    @click="selectStatus(props.row, s.value)"
                                                >
                                                    <span class="status-dot"></span>
                                                    {{ s.text }}
                                                    <fa v-if="props.row.status === s.value" :icon="['fas', 'check']" class="status-check" />
                                                </div>
                                            </div>
                                        </div>
                                    </span>
                                    <span v-else-if="props.column.field === 'status'" class="status-cell">
                                        <span class="status-badge status-badge--readonly" :class="statusClass(props.row.status)">
                                            <span class="status-dot"></span>
                                            {{ props.row.status || '—' }}
                                        </span>
                                    </span>
                                    <span v-else-if="props.column.field == 'created_date'" style="width: 50px;">
                                        {{ new Date(props.row.created_date).toLocaleDateString(language, {
                                            month:
                                                '2-digit', day: '2-digit', year: 'numeric'
                                        }) + " , " + new
                                            Date(props.row.created_date).toLocaleTimeString(language, {hour: '2-digit',
                                        minute:'2-digit'}) }}
                                    </span>
                                    <span
                                        v-else-if="props.column.field == 'delivery_date' && props.row.delivery_date != null"
                                        style="width: 50px;">
                                        {{ new Date(props.row.delivery_date).toLocaleDateString(language, {
                                            month:
                                                '2-digit', day: '2-digit', year: 'numeric'}) }}
                                    </span>
                                    <span v-else style="width: 50px;">
                                        {{ props.row[props.column.field] }}
                                    </span>
                                </template>
                            </vue-good-table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="copyright">
            <a href="https://dmp-maps.com/legal/" class="attribution" target="_blank">© {{ new Date().getFullYear() }}
                Dynamic
                Map Platform North America, Inc. All Rights Reserved</a>
        </div>
        </div><!-- /.dashboard-scroll -->
        </div><!-- /.dashboard-main -->
        <div class="notif-drawer" :class="{ 'notif-drawer--open': notifPanelOpen }">
            <TRT_Notifications @close="notifPanelOpen = false" @unread-count="notifUnreadCount = $event" :user="user" />
        </div>
        <DT_StatsModal :row="selectedRow" :lidarFlag="true"></DT_StatsModal>
    </div>
</template>

<style scoped>
.nav-left {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
}

.nav-back {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border-radius: 6px;
    color: #4A74A8;
    cursor: pointer;
    font-size: 1rem;
    transition: background 0.15s ease, color 0.15s ease;
}

.nav-back:hover {
    background: #eef3f9;
    color: #063c6d;
}

.nav-separator {
    color: #adb5bd;
    font-size: 1rem;
    user-select: none;
}

.nav-page {
    display: flex;
    align-items: center;
    gap: 0.3rem;
    font-size: 0.92rem;
    color: #6c757d;
    padding: 0.25rem 0.6rem;
    border-radius: 6px;
}

.active-page {
    color: #063c6d;
    font-weight: 600;
    background: #eef3f9;
    border: 1px solid rgba(74, 116, 168, 0.2);
}

/* Larger / Monitor Screen View */
@media (min-width: 1700px) {
    .userInfo {
        display: flex;
        justify-content: flex-end;
        align-items: center;
        flex-direction: row;
        width: 100%;
        color: #063c6d;
        font-size: 1rem;
        gap: 0.625rem;
    }

    .chart-margin {
        margin-top: 1rem !important;
    }

    .ptv-table-scroll {
        overflow-y: auto !important;
    }
}

/* Laptop Screen View */
@media (max-width: 1700px) {
    .userInfo {
        display: flex;
        justify-content: flex-end;
        align-items: center;
        flex-direction: row;
        width: 100%;
        color: #063c6d;
        font-size: 1.0625rem;
        gap: 0.625rem;
    }

    .chart-margin {
        margin-top: 0.5rem !important;
    }

    .ptv-table-scroll {
        overflow-y: auto !important;
    }
}

#page-content-wrapper {
    flex: 1;
    overflow: hidden;
    background-color: #f8f9fa;
    display: flex;
    flex-direction: row;
}

.dashboard-main {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.dashboard-scroll {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding-left: 1.25rem;
    padding-right: 1.25rem;
}

/* ── Fixed top nav ── */
.dt-fixed-nav {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.65rem 1.5rem;
    background: rgba(255, 255, 255, 0.75);
    backdrop-filter: blur(16px) saturate(180%);
    -webkit-backdrop-filter: blur(16px) saturate(180%);
    border-bottom: 1px solid rgba(6, 60, 109, 0.08);
    box-shadow: 0 1px 12px rgba(6, 60, 109, 0.06);
}

.dt-user-info {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    color: #063c6d;
}

.dt-user-avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 50%;
    background: linear-gradient(135deg, #0a548f 0%, #063c6d 100%);
    box-shadow: 0 2px 8px rgba(6, 60, 109, 0.25);
    overflow: hidden;
    flex: 0 0 auto;
    color: #ffffff;
    font-size: 1rem;
}

.dt-user-greeting {
    display: flex;
    flex-direction: column;
    line-height: 1.2;
}

.dt-user-greeting-label {
    font-size: 0.72rem;
    font-weight: 500;
    color: #8ab0cc;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.dt-user-greeting-name {
    font-size: 1.02rem;
    font-weight: 700;
    color: #063c6d;
}

.dt-nav-actions {
    display: flex;
    align-items: center;
    gap: 0.625rem;
}

.dt-nav-btn {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.625rem;
    height: 2.625rem;
    border: 1px solid rgba(6, 60, 109, 0.1);
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.6);
    color: #4A74A8;
    font-size: 1.05rem;
    cursor: pointer;
    transition: background 0.18s ease, color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.dt-nav-btn:hover {
    background: #ffffff;
    color: #063c6d;
    box-shadow: 0 4px 14px rgba(6, 60, 109, 0.14);
    transform: translateY(-1px);
}

.dt-nav-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.dt-nav-btn:disabled:hover {
    background: rgba(255, 255, 255, 0.6);
    color: #4A74A8;
    box-shadow: none;
    transform: none;
}

.dt-signout-btn:hover {
    background: #fff;
    color: #dc3545;
    box-shadow: 0 4px 14px rgba(220, 53, 69, 0.18);
}

.notif-drawer {
    flex-shrink: 0;
    width: 0;
    overflow: hidden;
    background: #fff;
    border-left: 1px solid #dee2e6;
    transition: width 0.28s cubic-bezier(0.4, 0, 0.2, 1);
}

.notif-drawer--open {
    width: 21.25rem;
}

.notif-bell-btn {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.375rem;
    height: 2.375rem;
    border: 1px solid rgba(74, 116, 168, 0.25);
    border-radius: 8px;
    background: #fff;
    color: #4A74A8;
    font-size: 1rem;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.notif-bell-btn:hover,
.notif-bell-btn--active {
    background: #eef3f9;
    color: #063c6d;
    box-shadow: 0 2px 8px rgba(74, 116, 168, 0.15);
}

.notif-bell-badge {
    position: absolute;
    top: -0.3125rem;
    right: -0.3125rem;
    min-width: 1.125rem;
    height: 1.125rem;
    padding: 0 0.25rem;
    border-radius: 9px;
    background: #dc3545;
    color: #fff;
    font-size: 0.65rem;
    font-weight: 700;
    line-height: 1.125rem;
    text-align: center;
    pointer-events: none;
}

.card h2 {
    font-size: 2rem;
    margin: 0;
}

.widgetColor {
    /* background-color: #1975d8; */
    background-color: #316fb1;
    border-radius: 2%;
}

.chart-placeholder {
    height: 18.75rem;
    background: repeating-linear-gradient(45deg,
            #f0f0f0,
            #f0f0f0 10px,
            #e0e0e0 10px,
            #e0e0e0 20px);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #555;
    font-size: 1.2rem;
}

.navbar {
    padding: 0.75rem 1.25rem;
}

.highlight {
    stroke: rgb(35, 223, 82);
    stroke-width: 2px;
    fill: rgba(159, 204, 153, 0.1);
}

.dmap {
    width: 100%;
    height: 25rem;
}

#dmap:focus {
    outline: #4A74A8 solid 0.15em;
}

.table-wrapper {
    background: #fff;
    border-radius: 5px;
    border: 1px solid #dee2e6;
    box-shadow: 0 2px 8px rgba(74, 116, 168, 0.1);
    overflow: hidden;
}

.table-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #4A74A8;
    border-bottom: 2px solid;
    padding: 0.7rem 1.25rem;
    color: #fff;
}

.table-header-left {
    display: flex;
    align-items: center;
    gap: 0.6rem;
}

.table-header-dot {
    display: inline-block;
    width: 0.625rem;
    height: 0.625rem;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.6);
    flex-shrink: 0;
}

.table-header-title {
    font-size: 0.88rem;
    font-weight: 700;
    color: #fff;
    text-transform: uppercase;
    letter-spacing: 0.6px;
}

.table-header-right {
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.85rem;
}

/* ── vue-good-table overrides ── */
:deep(.vgt-table) {
    border: none !important;
    border-collapse: separate;
    border-spacing: 0;
    width: 100%;
    font-size: 0.875rem;
}

:deep(.vgt-table thead th) {
    background-color: #f0f4f9 !important;
    color: #063c6d !important;
    font-weight: 600 !important;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 2px solid #4A74A8 !important;
    border-top: none !important;
    padding: 0.75rem 1rem !important;
    white-space: nowrap;
}

:deep(.vgt-table thead th input),
:deep(.vgt-table thead th select) {
    border: 1px solid #cdd8e8 !important;
    border-radius: 5px !important;
    font-size: 0.78rem !important;
    padding: 0.3rem 0.5rem !important;
    color: #063c6d;
    background: #fff;
    margin-top: 0.25rem;
}

:deep(.vgt-table thead th input:focus),
:deep(.vgt-table thead th select:focus) {
    border-color: #4A74A8 !important;
    outline: none !important;
    box-shadow: 0 0 0 2px rgba(74, 116, 168, 0.15) !important;
}

:deep(.vgt-table tbody tr) {
    transition: background 0.12s ease;
}

:deep(.vgt-table tbody tr:nth-child(even)) {
    background-color: #f8fafc !important;
}

:deep(.vgt-table tbody tr:hover) {
    background-color: #eef3f9 !important;
}

:deep(.vgt-table tbody td) {
    border-bottom: 1px solid #e9ecef !important;
    border-top: none !important;
    padding: 0.65rem 1rem !important;
    color: #344767;
    vertical-align: middle;
}

:deep(.vgt-wrap__footer) {
    background: #f0f4f9 !important;
    border-top: 1px solid #dee2e6 !important;
    padding: 0.5rem 1rem !important;
}

:deep(.vgt-wrap__footer .footer__row-count__label),
:deep(.vgt-wrap__footer .footer__row-count__select) {
    font-size: 0.8rem;
    color: #6c757d;
}

:deep(.vgt-wrap__footer .footer__navigation__page-btn) {
    color: #4A74A8 !important;
    font-weight: 500;
}

:deep(.vgt-wrap__footer .footer__navigation__page-btn:hover) {
    color: #063c6d !important;
}

:deep(.vgt-wrap__footer .footer__navigation__page-btn--active) {
    background-color: #4A74A8 !important;
    color: #fff !important;
    border-radius: 4px;
}

:deep(.vgt-wrap__footer .footer__navigation__page-info) {
    font-size: 0.8rem;
    color: #6c757d;
}

.btn-request-sample {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    background: linear-gradient(135deg, #497558 0%, #2b5a3b 100%);
    color: #fff;
    border: 1px solid rgba(50, 150, 80, 0.3);
    border-radius: 8px;
    padding: 0.625rem 1.375rem;
    font-size: 0.88rem;
    font-weight: 700;
    letter-spacing: 0.4px;
    white-space: nowrap;
    box-shadow: 0 4px 14px rgba(6, 46, 20, 0.45);
    transition: all 0.2s ease;
}

.btn-request-sample:hover {
    background: linear-gradient(135deg, #1a7a38 0%, #0d5c28 100%);
    box-shadow: 0 6px 20px rgba(13, 92, 40, 0.5);
    border-color: rgba(50, 150, 80, 0.55);
    color: #fff;
    transform: translateY(-1px);
}

.btn-request-sample:active {
    background: linear-gradient(135deg, #062e14 0%, #031a0a 100%);
    box-shadow: none;
    transform: translateY(0);
    color: #fff;
}

.copyright {
    position: fixed;
    bottom: 0.625rem !important;
    right: 0.75rem;
    z-index: 5;
}

.attribution {
    display: inline-flex;
    align-items: center;
    padding: 0.3125rem 0.875rem;
    color: #063c6d !important;
    font-size: 0.8125rem;
    font-weight: 600;
    letter-spacing: 0.01em;
    text-decoration: none !important;
    background: rgba(255, 255, 255, 0.55);
    backdrop-filter: blur(8px);
    border: none;
    border-radius: 999px;
    transition: color 0.15s ease, background 0.15s ease;
}

.attribution:hover {
    color: #063c6d !important;
    background: rgba(255, 255, 255, 0.8);
    text-decoration: none !important;
}

a:hover {
    color: #0056b3 !important;
    text-decoration: underline !important;
}

.mmpInfo {
    width: 12.5rem;
    color: #063c6d;
    font-size: 1.0625rem;
    cursor: pointer;
}

.mmpInfo:hover {
    text-decoration: underline;
    color: #063c6d;
}
/* ── Dashboard header ── */
.dash-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
}
.dash-header-actions {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}
.dash-title {
    font-size: 1.1rem;
    font-weight: 700;
    color: #063c6d;
    margin: 0;
}
.dash-subtitle {
    font-size: 0.78rem;
    color: #6c757d;
    margin: 0;
}

/* ── Status badge ── */
.status-cell {
    display: inline-flex;
    align-items: center;
}

.status-wrapper {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
}

.status-save-indicator {
    display: inline-flex;
    align-items: center;
    font-size: 0.72rem;
    color: #6c757d;
}

.status-save-indicator--saved {
    color: #28a745;
}

.status-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.25rem 0.625rem 0.25rem 0.5rem;
    border-radius: 20px;
    font-size: 0.73rem;
    font-weight: 600;
    letter-spacing: 0.3px;
    cursor: pointer;
    transition: filter 0.15s ease, box-shadow 0.15s ease;
    user-select: none;
    white-space: nowrap;
}

.status-badge:hover {
    filter: brightness(0.93);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.badge-open {
    box-shadow: 0 0 0 2px rgba(74, 116, 168, 0.35) !important;
}

.status-dot {
    width: 0.4375rem;
    height: 0.4375rem;
    border-radius: 50%;
    background: currentColor;
    opacity: 0.65;
    flex-shrink: 0;
}

.status-chevron {
    font-size: 0.6rem;
    opacity: 0.55;
    margin-left: 1px;
    transition: transform 0.15s ease;
}

.chevron-open {
    transform: rotate(180deg);
}

.status-success { background: #d4edda; color: #155724; border: 1px solid #b8dfc4; }
.status-info    { background: #d1ecf1; color: #0c5460; border: 1px solid #a8d9e4; }
.status-warning { background: #fff3cd; color: #7d5a00; border: 1px solid #fce5a0; }
.status-danger  { background: #f8d7da; color: #721c24; border: 1px solid #f1b8be; }
.status-hold    { background: #e9d8fd; color: #4a1a8a; border: 1px solid #d6b8f8; }
.status-default { background: #e2e8f0; color: #374151; border: 1px solid #c9d3e0; }
.status-unknown { background: #f1f3f5; color: #6c757d; border: 1px solid #dee2e6; }

/* ── Status custom dropdown ── */
.status-dropdown {
    position: fixed;
    z-index: 9999;
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 10px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.13), 0 2px 8px rgba(0, 0, 0, 0.08);
    padding: 0.25rem;
    min-width: 10rem;
    animation: statusDropIn 0.12s ease;
}

@keyframes statusDropIn {
    from { opacity: 0; transform: translateY(-4px); }
    to   { opacity: 1; transform: translateY(0); }
}

.status-option {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.3125rem 0.625rem 0.3125rem 0.5rem;
    border-radius: 7px;
    font-size: 0.73rem;
    font-weight: 600;
    letter-spacing: 0.3px;
    cursor: pointer;
    margin: 2px 0;
    transition: filter 0.1s ease;
}

.status-option:hover {
    filter: brightness(0.9);
}

.status-check {
    margin-left: auto;
    font-size: 0.65rem;
}

.notif-bell-btn--pulse {
    animation: bellPulse 1.6s infinite;
}

@keyframes bellPulse {
    0% {
        box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.6);
    }
    70% {
        box-shadow: 0 0 0 10px rgba(220, 53, 69, 0);
    }
    100% {
        box-shadow: 0 0 0 0 rgba(220, 53, 69, 0);
    }
}
.status-badge--readonly {
    cursor: default;
}

.status-badge--readonly:hover {
    filter: none;
    box-shadow: none;
}
</style>

<script>
// OpenLayers imports
import axios from 'axios'
import DT_StatsModal from './DT_StatsModal.vue';
import TRT_Notifications from './TRT_Notifications.vue';
import { mapGetters } from 'vuex';

import lidar_sample_status_change from '!!raw-loader!../../../emails/lidar_sample_status_change.html'

export default {
    name: 'Dashboard',
    components: {
        DT_StatsModal,
        TRT_Notifications
    },
    data() {
        return {
            notifPanelOpen: false,
            notifUnreadCount: 0,
            naCount: null,
            meCount: null,
            euCount: null,
            jpCount: null,
            rows: [],
            classes: {
                table: {
                    //   'menu-bg-blue-transparent': true
                }
            },
            userOptions: [],
            filteredRows: [],
            filters: {
                created_date: null,
                delivery_date: null,
                created_by: null,
                status: null
            },
            config: {
                // checkbox_rows: false,
                // rows_selectable: true,
                // card_mode: false,
                global_search: {
                    placeholder: 'Search...',
                    visibility: true,
                    showClearButton: true,
                    searchOnPressEnter: true
                },
                // highlight_row_hover_color: '#4a4a4a',
                show_refresh_button: false,
                show_reset_button: false
            },
            pagination: {
                enabled: true,
                perPage: 10,
                perPageDropdownEnabled: false,
            },
            search: {
                enabled: true
            },
            selectedRow: null,
            language: navigator.language,
            tableHeight: '400px',
            statuses: [],
            editingStatusRowId: null,
            statusDropdownPos: { top: 0, left: 0 },
            statusSaving: {},
            emailRecipients: [],
            prevStatus: null
        }
    },
    props: [
        'user',
        'lidarFlag'
    ],
    methods: {
        signOut() {
            sessionStorage.removeItem('token')
            sessionStorage.removeItem('pid')
            this.$emit('clearCurrentPositionsInterval')
            this.$router.push({ name: 'Login' })
        },
        formatToMMDDYYYY(value) {
            if (!value) return '';
            const d = new Date(value);
            // Adjust for timezone offset (treat as local date)
            const local = new Date(d.getTime() + d.getTimezoneOffset() * 60000);

            const mm = String(local.getMonth() + 1).padStart(2, '0');
            const dd = String(local.getDate()).padStart(2, '0');
            const yyyy = local.getFullYear();
            return `${mm}/${dd}/${yyyy}`;
        },

        applyAllFilters() {
            const { created_date, delivery_date, created_by, status } = this.filters;

            this.filteredRows = this.rows.filter(row => {
                // Filter by Created By
                if (created_by && row.created_by !== created_by) return false;

                // Filter by Created Date
                if (created_date) {
                    const rowDate = this.formatToMMDDYYYY(row.created_date);
                    const filterDate = this.formatToMMDDYYYY(created_date);
                    if (rowDate !== filterDate) return false;
                }

                // Filter by Delivery Date
                if (delivery_date) {
                    const rowDate = this.formatToMMDDYYYY(row.delivery_date);
                    const filterDate = this.formatToMMDDYYYY(delivery_date);
                    if (rowDate !== filterDate) return false;
                }

                if (status) {
                    if (row.status !== status) return false;
                }

                return true;
            });
        },

        dateFilter(val, field) {
            this.filters[field] = val;
            this.applyAllFilters();
        },
        initStatsModal(row) {
            this.selectedRow = row
            this.$bvModal.show('ptvStatsModal')
        },
        getSampleData() {
            if (sessionStorage.token) {
                axios
                    .get(process.env.API_LOCATION + '/api/trailReviewTool/get_requested_lidar_data', {
                        method: 'GET',
                        headers: {
                            'x-auth-token': sessionStorage.token
                        }
                    })
                    .then(response => {
                        this.rows = response.data
                        this.filteredRows = this.rows
                        if (this.user && !this.user.isAdmin) {
                            this.filteredRows = this.rows.filter(row => row.created_by == this.user.name)
                            this.filters.created_by = this.user.name
                        } else {
                            this.userOptions = [...new Set(this.rows.map(r => r.created_by))].sort();
                        }
                    })
                    .catch(function (error) {
                        console.log(error)
                    })
            } else {
                console.log('No User Token')
            }
        },
        callMap() { 
            if (this.$route.query.dashboard) {
                this.$router.replace({
                    name: this.$route.name,
                    query: {}
                })
            }
            this.$emit('loadProject', 'Lidar') 
        },
        updateTableHeight() {
            // Subtract: navbar (~52px), page padding (~40px), button (~48px),
            // chart-margin (~32px), card-header (~48px), card-body padding (~24px), copyright (~30px)
            const offset = 274;
            const height = Math.max(200, window.innerHeight - offset);
            this.tableHeight = `${height}px`;
        },
        getStatusLabels() {
            if (sessionStorage.token) {
                axios
                    .get(process.env.API_LOCATION + '/api/trailReviewTool/get_lidar_sample_statuses', {
                        method: 'GET',
                        headers: {
                            'x-auth-token': sessionStorage.token
                        }
                    })
                    .then(response => {
                        const content = response.data
                        this.statuses = content.map(s => ({
                            value: s.enumlabel,
                            text: s.enumlabel
                        }))
                    })
                    .catch(function (error) {
                        console.log(error)
                    })
            } else {
                console.log('No User Token')
            }
        },
        statusClass(status) {
            if (!status) return 'status-unknown';
            const s = status.toLowerCase();
            if (s.includes('complet') || s.includes('deliver') || s.includes('done')) return 'status-success';
            if (s.includes('progress') || s.includes('process') || s.includes('active')) return 'status-info';
            if (s.includes('pending') || s.includes('queue') || s.includes('wait')) return 'status-warning';
            if (s.includes('cancel') || s.includes('reject') || s.includes('fail')) return 'status-danger';
            if (s.includes('hold') || s.includes('pause')) return 'status-hold';
            return 'status-default';
        },
        toggleStatusDropdown(rowId, event) {
            if (this.editingStatusRowId === rowId) {
                this.closeStatusDropdown();
                return;
            }
            const rect = event.currentTarget.getBoundingClientRect();
            this.statusDropdownPos = { top: rect.bottom + 4, left: rect.left };
            this.editingStatusRowId = rowId;
            this.$nextTick(() => {
                document.addEventListener('click', this.closeStatusDropdown);
            });
        },
        closeStatusDropdown() {
            this.editingStatusRowId = null;
            document.removeEventListener('click', this.closeStatusDropdown);
        },
        selectStatus(row, value) {
            this.prevStatus = row.status;
            row.status = value;
            this.closeStatusDropdown();
            this.$set(this.statusSaving, row.id, { spinner: true, saved: false });
            this.updateStatus(row);
        },
        updateStatus(row) {
            const body = { id: row.id, status: row.status };

            if (sessionStorage.token) {
                axios
                    .put(process.env.API_LOCATION + '/api/trailReviewTool/updateSampleStatus', body, {
                        headers: { 'x-auth-token': sessionStorage.token }
                    })
                    .then(response => {
                        const content = response.data;
                        if (content.error) {
                            console.log(content.error);
                            this.$set(this.statusSaving, row.id, null);
                        } else {
                            const state = this.statusSaving[row.id];
                            if (state) state.saved = true;
                            this.formulateNotification(row)
                            this.sendEmail(row)
                            setTimeout(() => { this.$set(this.statusSaving, row.id, null); }, 2500);
                        }
                    })
                    .catch(error => {
                        console.log(error);
                        this.$set(this.statusSaving, row.id, null);
                    });
            } else {
                console.log('No User Token');
                this.$set(this.statusSaving, row.id, null);
            }
        },
        formulateNotification(row) {
            let msg = `The status of your sample request has changed to: ${row.status}.\n\n`

            switch (row.status) {
                case 'Reviewing':
                    msg += 'Current Stage: Your request is being reviewed!'
                    break;
                case 'Accepted':
                    msg += 'Current Stage: Your request has been accepted and is waiting for processing to being.'
                    break;
                case 'Processing':
                    msg += 'Current Stage: Your data is currently being prepared.'
                    break;
                case 'Delivered':
                    msg += 'Current Stage: Your sample is ready for access!'
                    break;
            }

            if (row.status != 'Delivered' && row.status != null) {
                msg += '\n\nWe will notify you of any further updates as your request moves forward.'
            }

            this.$emit('notification', { msg: msg, user_id: row.created_user_id})
        },
        async getUserEmail(row) {
            if (sessionStorage.token) {
                await axios
                    .get(process.env.API_LOCATION + '/api/users/user', {
                        headers: { 'x-auth-token': sessionStorage.token },
                        params: {
                            id: row.created_user_id
                        }
                    })
                    .then(response => {
                        const content = response.data;
                        if (content.error) {
                            console.log(content.error);
                        } else {
                            this.emailRecipients = content.rows[0]
                        }
                    })
                    .catch(error => {
                        console.log(error);
                    });
            } else {
                console.log('No User Token');
            }
        },
        async sendEmail(row) {
            await this.getUserEmail(row)      

            const fromEmail = 'mmp-info@dmp-maps.com'
            const toEmail = [this.emailRecipients.email]
            const emailSubject = 'Your LiDAR Sample status has changed'
            let year = new Date().getFullYear()
            let emailBody = lidar_sample_status_change

            // Replace basic fields
            emailBody = emailBody
                .replace('{{ name }}', this.emailRecipients.name)
                .replace('{{ oldStatus }}', this.prevStatus)
                .replace('{{ newStatus }}', row.status)
                .replace('{{ submitted_date }}', row.created_date.split('T')[0])
                .replace('{{ delivery_type }}', row.delivery_type)
                .replace('{{ delivery_date }}', row.delivery_date.split('T')[0])
                .replace('{{ region }}', row.region)
                .replace('{{ all_segments_length }}', row.trails_length)
                .replace('{{ osm_length }}', row.osm_length)
                .replace('{{ trails }}', row.trails.join('\n'))
                .replace('{{ year }}', year)

            if (sessionStorage.token) {
                axios.post(
                    process.env.API_LOCATION + '/api/email/sendemail',
                    {
                        fromEmail,
                        toEmail,
                        emailSubject,
                        emailBody
                    },
                    {
                        headers: {
                            'x-auth-token': sessionStorage.token
                        }
                    }
                )
                .then(response => {
                    const content = response.data

                    if (!content.errors) {
                        this.$bvToast.toast('LiDAR Sample Status Change email sent.', {
                            autoHideDelay: 5000,
                            variant: 'success',
                            solid: true,
                            title: 'Email Sent',
                            toaster: 'b-toaster-top-right'
                        })
                    }
                })
                .catch(error => {
                    console.log(error)
                })

            } else {
                console.log("No User Token")
                this.$router.push({ name: 'Login' })
            }
        }
    },
    mounted() {
        this.$emit('loadProject', 'Dashboard')
        this.getStatusLabels();
        this.getSampleData();
        this.updateTableHeight();
        window.addEventListener('resize', this.updateTableHeight);
        window.addEventListener('resize', this.closeStatusDropdown);
        this.$nextTick(() => {
            if (this.$refs.tableScroll) {
                this.$refs.tableScroll.addEventListener('scroll', this.closeStatusDropdown);
            }
        });
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.updateTableHeight);
        window.removeEventListener('resize', this.closeStatusDropdown);
        document.removeEventListener('click', this.closeStatusDropdown);
        if (this.$refs.tableScroll) {
            this.$refs.tableScroll.removeEventListener('scroll', this.closeStatusDropdown);
        }
    },
    watch: {
        user: function () {
            if (!this.user.isAdmin) {
                this.filteredRows = this.rows.filter(row => row.created_by == this.user.name)
                this.filters.created_by = this.user.name
            } else {
                this.userOptions = [...new Set(this.rows.map(r => r.created_by))].sort();
            }
        }
    },
    computed: {
        ...mapGetters(['globalUserRole', 'globalAllowedUserModules', 'globalIsUserAllowed']),
        isTestAccount() {
            return this.user && this.user.test
        },
        tableColumns() {
            const baseColumns = [
                { label: 'ID', field: 'id', sortable: true, sortFn: (x, y) => Number(x) - Number(y) },
                { label: 'Requested by', field: 'created_by', sortable: true },
                {
                    label: 'Requested Date',
                    field: 'created_date',
                    sortable: true,
                    filterOptions: { enabled: true, customFilter: true }
                },
                { label: 'Region', field: 'region', sortable: true },
                { label: 'Segment Length', field: 'trails_length', sortable: true },
                { label: 'Delivery Type', field: 'delivery_type', sortable: true },
                {
                    label: 'Requested Delivery Date',
                    field: 'delivery_date',
                    sortable: true,
                    filterOptions: { customFilter: true }
                },
                {
                    label: 'Request Info',
                    field: 'wkt',
                    sortable: false,
                    width: '140px',
                    tdClass: 'text-center',
                    thClass: 'text-center'
                }
            ];

            baseColumns.push({
                label: 'Status',
                field: 'status',
                sortable: true,
                ...(this.user && this.user.isAdmin
                    ? { filterOptions: { enabled: true, customFilter: true } }
                    : {})
            });

            return baseColumns;
        }
    }
}
</script>